import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useDealStore } from '@/stores/deal-store'
import { calculateDealMetrics, type Deal, type CalculatedMetrics } from '@/lib/schema'
import { formatCurrency, formatPercent, formatNumber } from '@/lib/formulas'
import { clsx } from 'clsx'
import { Plus, X } from 'lucide-react'

interface DealWithMetrics {
  deal: Deal
  metrics: CalculatedMetrics
}

export function ComparePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { deals, fetchDeals, isLoading } = useDealStore()
  const [selectedDeals, setSelectedDeals] = useState<string[]>([])

  useEffect(() => {
    fetchDeals()
  }, [fetchDeals])

  useEffect(() => {
    const dealIds = searchParams.get('deals')?.split(',').filter(Boolean) || []
    setSelectedDeals(dealIds)
  }, [searchParams])

  const updateSelectedDeals = (ids: string[]) => {
    setSelectedDeals(ids)
    if (ids.length > 0) {
      setSearchParams({ deals: ids.join(',') })
    } else {
      setSearchParams({})
    }
  }

  const addDeal = (id: string) => {
    if (selectedDeals.length < 3 && !selectedDeals.includes(id)) {
      updateSelectedDeals([...selectedDeals, id])
    }
  }

  const removeDeal = (id: string) => {
    updateSelectedDeals(selectedDeals.filter((d) => d !== id))
  }

  const comparedDeals: DealWithMetrics[] = selectedDeals
    .map((id) => deals.find((d) => d.id === id))
    .filter((d): d is Deal => d !== undefined)
    .map((deal) => ({ deal, metrics: calculateDealMetrics(deal) }))

  const availableDeals = deals.filter((d) => !selectedDeals.includes(d.id))

  if (isLoading && deals.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  const metrics = [
    { key: 'purchasePrice', label: 'Purchase Price', format: formatCurrency, better: 'lower' },
    { key: 'monthlyRent', label: 'Monthly Rent', format: formatCurrency, better: 'higher' },
    { key: 'capRate', label: 'Cap Rate', format: formatPercent, better: 'higher' },
    { key: 'cashOnCash', label: 'Cash-on-Cash', format: formatPercent, better: 'higher' },
    { key: 'monthlyCashFlow', label: 'Monthly Cash Flow', format: formatCurrency, better: 'higher' },
    { key: 'annualCashFlow', label: 'Annual Cash Flow', format: formatCurrency, better: 'higher' },
    { key: 'dscr', label: 'DSCR', format: (v: number) => formatNumber(v), better: 'higher' },
    { key: 'grm', label: 'GRM', format: (v: number) => formatNumber(v), better: 'lower' },
    { key: 'totalCashInvested', label: 'Cash Invested', format: formatCurrency, better: 'lower' },
    { key: 'noi', label: 'NOI (Annual)', format: formatCurrency, better: 'higher' },
    { key: 'monthlyPayment', label: 'Monthly P&I', format: formatCurrency, better: 'lower' },
  ]

  const getMetricValue = (dealWithMetrics: DealWithMetrics, key: string): number => {
    const { deal, metrics } = dealWithMetrics
    if (key in deal) return deal[key as keyof Deal] as number
    if (key in metrics) return metrics[key as keyof CalculatedMetrics] as number
    return 0
  }

  const getBestValue = (key: string, better: string): number | null => {
    if (comparedDeals.length === 0) return null
    const values = comparedDeals.map((d) => getMetricValue(d, key))
    return better === 'higher' ? Math.max(...values) : Math.min(...values)
  }

  return (
    <div>
      <div className="mb-6">
        <h1>Compare Deals</h1>
        <p className="mt-1 text-slate-500">Select up to 3 deals to compare side-by-side.</p>
      </div>

      {/* Deal Selector */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-medium text-slate-900">Selected Deals ({selectedDeals.length}/3)</h3>

        <div className="mt-4 flex flex-wrap gap-2">
          {comparedDeals.map(({ deal }) => (
            <div
              key={deal.id}
              className="flex items-center gap-2 rounded-lg bg-primary-50 px-3 py-2"
            >
              <Link to={`/deals/${deal.id}`} className="text-sm font-medium text-primary-700 hover:underline">
                {deal.name || deal.address}
              </Link>
              <button
                onClick={() => removeDeal(deal.id)}
                className="text-primary-400 hover:text-primary-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}

          {selectedDeals.length < 3 && availableDeals.length > 0 && (
            <div className="relative">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    addDeal(e.target.value)
                    e.target.value = ''
                  }
                }}
                className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-600 hover:border-primary-400 hover:text-primary-600"
                defaultValue=""
              >
                <option value="" disabled>
                  + Add deal
                </option>
                {availableDeals.map((deal) => (
                  <option key={deal.id} value={deal.id}>
                    {deal.name || deal.address}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {deals.length === 0 && (
          <p className="mt-4 text-sm text-slate-500">
            No deals available.{' '}
            <Link to="/deals/new" className="text-primary-600 hover:underline">
              Create your first deal
            </Link>{' '}
            to start comparing.
          </p>
        )}
      </div>

      {/* Comparison Table */}
      {comparedDeals.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Metric</th>
                {comparedDeals.map(({ deal }) => (
                  <th key={deal.id} className="px-4 py-3 text-right text-sm font-medium text-slate-900">
                    <Link to={`/deals/${deal.id}`} className="hover:text-primary-600 hover:underline">
                      {deal.name || deal.address}
                    </Link>
                    <p className="mt-0.5 font-normal text-slate-500">
                      {deal.city}, {deal.state}
                    </p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, idx) => {
                const bestValue = getBestValue(metric.key, metric.better)

                return (
                  <tr
                    key={metric.key}
                    className={clsx(
                      'border-b border-slate-100 last:border-0',
                      idx % 2 === 0 && 'bg-slate-50/50'
                    )}
                  >
                    <td className="px-4 py-3 text-sm text-slate-600">{metric.label}</td>
                    {comparedDeals.map(({ deal, metrics: m }) => {
                      const value = getMetricValue({ deal, metrics: m }, metric.key)
                      const isBest = value === bestValue && comparedDeals.length > 1

                      return (
                        <td
                          key={deal.id}
                          className={clsx(
                            'px-4 py-3 text-right font-mono text-sm',
                            isBest ? 'font-semibold text-success-600' : 'text-slate-900'
                          )}
                        >
                          {metric.format(value)}
                          {isBest && comparedDeals.length > 1 && (
                            <span className="ml-1 text-xs text-success-500">Best</span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {comparedDeals.length === 0 && deals.length > 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-200 p-12 text-center">
          <Plus className="mx-auto h-12 w-12 text-slate-300" />
          <h3 className="mt-4 text-lg font-medium text-slate-900">No deals selected</h3>
          <p className="mt-2 text-slate-500">Select deals above to start comparing.</p>
        </div>
      )}
    </div>
  )
}
